import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Brand, LanguageToggle } from "../components/Brand";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError(t("new_password_min")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center justify-between">
          <Brand />
          <LanguageToggle />
        </div>
        <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900">{t("reset_password")}</h1>

        {!token ? (
          <div className="mt-6 text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">
            Lien invalide
          </div>
        ) : done ? (
          <div className="mt-6 space-y-4" data-testid="reset-success">
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
              {t("password_reset_success")}
            </div>
            <Link to="/login" className="text-blue-700 text-sm hover:underline">{t("go_to_login")}</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("new_password")}
                data-testid="reset-password-input"
                className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>
            {error && <div className="text-sm text-rose-600" data-testid="reset-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("update_password")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
